import { createContext } from 'react'
import * as msal from '@azure/msal-browser'
import axios from 'axios'
import { InteractionRequiredAuthError, LogLevel } from '@azure/msal-common'
import store from '../redux/store'
import { sendActivityLog } from '../api/activityLog.service'
import { events, pages, sections } from '../common/activityLog'

export class AuthService {
  constructor() {
    this.providerSelected = store.getState().provider
    this.config = {
      auth: {
        authority: process.env.REACT_APP_MSAL_AUTHORITY + process.env.REACT_APP_MSAL_LOGIN_POLICY,
        clientId: process.env.REACT_APP_MSAL_CLIENTID,
        validateAuthority: true,
        loginRedirect: process.env.REACT_APP_MSAL_LOGIN_REDIRECT,
        navigateToLoginRequestUrl: false,
        postLogoutRedirectUri: process.env.REACT_APP_MSAL_LOGIN_REDIRECT,
        redirectUri: process.env.REACT_APP_MSAL_LOGIN_REDIRECT
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true
      },
      system: {
        loggerOptions: {
          loggerCallback: this.logCallback,
          piiLoggingEnabled: true
        },
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
        asyncPopups: false
      }
    }
    this.loginRequest = {
      scopes: [process.env.REACT_APP_MSAL_SCOPE]
    }
    this.msalInstance = new msal.PublicClientApplication(this.config)
    this.msalInstance
      .handleRedirectPromise()
      .then((result) => {
        if (result) {
          // true = app returned from a redirect operation and it completed successfully
          sendActivityLog({
            sectionId: sections.Login,
            pageId: pages.Login,
            eventId: events.UserLogin,
            providerId: this.providerSelected.id
          })
            .then(() => {})
            .catch((error) => {
              console.error('Error sending activity log:', error)
            })
        } else {
          // false = app is not returning from a redirect operation (e.g. user visiting the site for the first time)
        }
      })
      .catch((error) => {
        this.logCallback(LogLevel.Error, error, false)
      })

    this.registerAxiosInterceptor()
  }

  logCallback(level, message, containsPii) {
    if (containsPii) {
      return
    }
    switch (level) {
      case LogLevel.Error:
        console.error(message)
        return
      case LogLevel.Info:
        //console.info(message)
        return
      case LogLevel.Verbose:
        //console.debug(message)
        return
      case LogLevel.Warning:
        //console.warn(message)
        return
      default:
        console.error(message)
        return
    }
  }

  registerAxiosInterceptor() {
    axios.interceptors.request.use(
      async (config) => {
        const session = await this.getToken()
        if (session.idToken) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${session.idToken}`,
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
          if (this.providerSelected && this.providerSelected.id) {
            config.headers['providerId'] = this.providerSelected.id
          }
          return config
        }
        return config
      },
      function (err) {
        return Promise.reject(err)
      }
    )
  }

  async getToken() {
    const currentAccount = this.getUserAccount()
    if (!currentAccount) {
      await this.signIn()
    } else {
      const silentRequest = {
        scopes: this.loginRequest.scopes,
        account: currentAccount,
        forceRefresh: false
      }
      const request = {
        scopes: this.loginRequest.scopes,
        loginHint: currentAccount.username
      }

      return this.msalInstance
        .acquireTokenSilent(silentRequest)
        .then((response) => {
          return response
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            return this.msalInstance.acquireTokenRedirect(request)
          }
        })
    }
  }

  signIn() {
    return this.msalInstance.loginRedirect(this.loginRequest)
  }

  async signOut() {
    const currentAccount = this.getUserAccount()
    await this.msalInstance
      .logoutRedirect({
        account: currentAccount
      })
      .then(() => {
        localStorage.clear()
        sessionStorage.clear()
      })
      .catch((reason) => {
        this.logCallback(LogLevel.Error, reason, false)
      })
  }

  getUserAccount() {
    const account = this.msalInstance.getAllAccounts()[0]
    return account === undefined ? null : account
  }

  isAuthenticated() {
    let accounts = this.msalInstance.getAllAccounts()
    return accounts.length > 0
  }
}

const AuthContext = createContext(null)
export default AuthContext
